import { createCreateFetchingKey } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";

/**
 * Jméno modulu menu. Slouží pro jednoznačnou identifikaci modulu.
 */
export const NAME = "menu";

const createFetchingKey = createCreateFetchingKey(NAME);

/**
 * Klíč pro indikaci načítání položek menu v fetching modulu.
 */
export const FETCHING_KEY_MENU_ITEMS = createFetchingKey("MENU_ITEMS");

/**
 * Klíč pro indikaci načítání notifikačních položek pro fetching modul.
 */
export const FETCHING_KEY_NOTIFICATION_ITEMS = createFetchingKey("FETCHING_KEY_NOTIFICATION_ITEMS");

/**
 * Klíč pro indikaci načítání lokalizace pro formulářové položky menu ve fetching modulu.
 */
export const FETCHING_KEY_FORM_ITEMS_LOCALIZATION = createFetchingKey("FETCHING_KEY_FORM_ITEMS_LOCALIZATION");

/**
 * Klíč pro indikaci načítání lokalizace pro notifikační skupiny ve fetching modulu.
 */
export const FETCHING_KEY_NOTIFICATION_GROUPS_LOCALIZATION = createFetchingKey("FETCHING_KEY_NOTIFICATION_GROUPS_LOCALIZATION");

/**
 * Klíč pro indikaci načítání lokalizace pro statické položky menu ve fetching modulu.
 */
export const FETCHING_KEY_STATIC_MENU_ITEMS_LOCALIZATION = createFetchingKey("FETCHING_KEY_STATIC_MENU_ITEMS_LOCALIZATION");

/**
 * Ikona zobrazovaná v případě, že formulář se zadaným id neexistuje.
 */
export const UNKNOWN_FORM_ICON_CODE = IconCode.uiHelp;

/**
 * Id rodiče pro kořenové položky formulářů.
 */
export const ROOT_ITEM_PARENT_ID = 0;

/**
 * Barva použitá pro neznámou (není ve stavu) notifikační položku.
 */
export const NO_NOTIFICATION_ITEM_ACTIVE_COLOR = "#BDBDBD";

/**
 * Kód ikony použitý pro neznámou (není ve stavu) notifikační položku.
 */
export const NO_NOTIFICATION_ITEM_ICON_CODE = IconCode.comMail;

/**
 * Nadpis použitý pro neznámou (není ve stavu) notifikační položku.
 */
export const NO_NOTIFICATION_ITEM_TITLE = "";

/**
 * Datum použité pro neznámou (není ve stavu) notifikační položku.
 */
export const NO_NOTIFICATION_ITEM_CREATED_DATE = new Date();

/**
 * Obsah zprávy použitý pro neznámou (není ve stavu) notifikační položku.
 */
export const NO_NOTIFICATION_ITEM_TEXT = "";

/**
 * Šířka menu v pixelech.
 */
export const MENU_WIDTH = 256;

/**
 * Šířka panelu menu v pixelech.
 */
export const MENU_PANEL_WIDTH = 64;

/**
 * Šířka rozšířeného panelu menu v pixelech.
 */
export const MENU_PANEL_WIDTH_EXTENDED = 80;

/**
 * Výška hlavičky menu notifikací, včetně paddingu.
 */
export const NOTIFICATIONS_HEADER_HEIGHT = 62;

/**
 * Výška hlavičky menu formulářů, včetně paddingu.
 */
export const FORMS_HEADER_HEIGHT = 110;

/**
 * Výška nadpisu skupiny v sekci Rychlý přístup.
 */
export const GROUP_HEADER_HEIGHT = 35;

/**
 * Výška patičky menu panelu, včetně paddingu.
 */
export const MENU_PANEL_FOOTER_HEIGHT = 104;

/** Výchozí výška položky formuláře */
export const DEFAULT_FORM_ITEM_HEIGHT = 35;

/**
 * Počet položek notifikačních položek načítaných v jedné dávce.
 * Počet není možné snížit kvůli správnému opětovnému volání algoritmu pro nekonečné scrollování.
 */
export const NOTIFICATIONS_PAGE_SIZE = 50;

/**
 * Id pole pro vyhledávané formuláře.
 */
export const FORM_SEARCH_INPUT_ID = "FORM_SEARCH_INPUT";

/**
 * Id tlačítka pro zobrazení všech notifikací.
 */
export const SHOW_ALL_NOTIFICATIONS_BUTTON_ID = "SHOW_ALL_NOTIFICATIONS_BUTTON";

/**
 * Id tlačítka pro označení všech notifikací skupiny za přečtené.
 */
export const SET_GROUP_NOTIFICATIONS_AS_READ_BUTTON_ID = "SET_GROUP_NOTIFICATIONS_AS_READ_BUTTON_ID";

/**
 * Id položky "Nastavení" v menu uživatele.
 */
export const USER_MENU_SETTINGS_ITEM_ID = "SETTINGS";

/**
 * Id položky "Odhlásit" v menu uživatele.
 */
export const USER_MENU_LOGOUT_ITEM_ID = "LOGOUT";

/**
 * Id položky pro přepnutí tématu v menu uživatele.
 */
export const USER_MENU_TOGGLE_THEME_ITEM_ID = "TOGGLE_THEME";

/**
 * Pomocná funkce pro vytvoření typu akce
 * @param actionType Typ akce
 */
const createActionType = (actionType: string) => `${NAME}/${actionType}`;

/** Konstanty typů akcí */
export const SET_MENU_ITEMS = createActionType("SET_MENU_ITEMS");
export const SET_ACTIVE_SUBMENU = createActionType("SET_ACTIVE_SUBMENU");
export const CLOSE_SUBMENUS = createActionType("CLOSE_SUBMENUS");
export const CLEAR_LAST_SUBMENU = createActionType("CLEAR_LAST_SUBMENU");
export const SET_ACTIVE_SECTION = createActionType("SET_ACTIVE_SECTION");
export const TOGGLE_SUBMENU = createActionType("TOGGLE_SUBMENU");
export const RESET_NOTIFICATION_ITEMS = createActionType("RESET_NOTIFICATION_ITEMS");
export const CONCAT_NOTIFICATION_ITEMS = createActionType("CONCAT_NOTIFICATION_ITEMS");
export const SET_ALLOWED_MENU_ITEM_CODES = createActionType("SET_ALLOWED_MENU_ITEM_CODES");
export const SET_FORM_ITEM_ACTION_RESTRICTIONS = createActionType("SET_FORM_ITEM_ACTION_RESTRICTIONS");
export const SET_PROFILE_ACTION_RESTRICTIONS = createActionType("SET_PROFILE_ACTION_RESTRICTIONS");
export const SET_NOTIFICATION_GROUPS = createActionType("SET_NOTIFICATION_GROUPS");
export const RESET_NOTIFICATIONS_PAGE_COUNT = createActionType("RESET_NOTIFICATIONS_PAGE_COUNT");
export const INCREASE_NOTIFICATIONS_PAGE_COUNT = createActionType("INCREASE_NOTIFICATIONS_PAGE_COUNT");
export const LOAD_NEXT_NOTIFICATION_ITEMS_PAGE = createActionType("LOAD_NEXT_NOTIFICATION_ITEMS_PAGE");
export const SHOW_GROUP_NOTIFICATIONS = createActionType("SHOW_GROUP_NOTIFICATIONS");
export const SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ = createActionType("SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ");
export const SET_MENU_VISIBILITY = createActionType("SET_MENU_VISIBILITY");
export const CONCAT_PANEL_ITEMS = createActionType("CONCAT_PANEL_ITEMS");
export const SET_NOTIFICATION_GROUPS_PANEL_ITEMS = createActionType("SET_NOTIFICATION_GROUPS_PANEL_ITEMS");
export const FETCHING_KEY_FORM_INSTANCE_TITLE = createActionType("FETCHING_KEY_FORM_INSTANCE_TITLE");
export const SET_MENU_PANEL_CAPTIONS_VISIBILITY = createActionType("SET_MENU_PANEL_CAPTIONS_VISIBILITY");
export const MENU_ITEM_ACTIVATED = createActionType("MENU_ITEM_ACTIVATED");
export const MENU_ITEM_DEFAULT_ACTION_ALLOWED = createActionType("MENU_ITEM_DEFAULT_ACTION_ALLOWED");
export const VISUAL_AUTOMAT_PROCESSED = createActionType("VISUAL_AUTOMAT_PROCESSED");
export const SET_VISUAL_AUTOMAT_STATE = createActionType("SET_VISUAL_AUTOMAT_STATE");
export const SET_MENU_FLOAT_TRANSITION_ACTIVE = createActionType("SET_MENU_FLOAT_TRANSITION_ACTIVE");
export const REMOVE_PROFILE_FROM_FORMS = createActionType("REMOVE_PROFILE_FROM_FORMS");
export const REMOVE_PROFILE_FORM_RELATIONSHIPS = createActionType("REMOVE_PROFILE_FORM_RELATIONSHIPS");
export const ADD_PROFILE_TO_RELATIONSHIPS = createActionType("ADD_PROFILE_TO_RELATIONSHIPS");
export const RELOAD_MENU = createActionType("RELOAD_MENU");
